import { InputDevice } from 'app/modules/wallforming/components/input-binding/types/inputDevice';
import { InputModifier } from 'app/modules/wallforming/components/input-binding/types/inputModifier';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import { MouseButton } from 'app/modules/wallforming/components/input-binding/types/mouseButton';

import type { InputChord } from 'app/modules/wallforming/components/input-binding/types/inputChord';
import type { KeyboardLayoutMap } from 'app/modules/wallforming/components/input-binding/types/keyboardLayoutMap';
import type { ParsedInputChord } from 'app/modules/wallforming/components/input-binding/types/parsedInputChord';

/**
 * Canonical chord string: `[Ctrl+][Shift+][Alt+][Meta+]<code>[@<trigger>]`, e.g. `Ctrl+KeyS`, `Shift+Mouse1@drag`, `Wheel`.
 * Codes use physical keys (`KeyboardEvent.code`), logical letters (`key:z`), `Mouse<button>` or `Wheel`.
 * The trigger is only used for mouse codes.
 * Chords are compared by string equality, which keeps runtime matching a single map lookup.
 */
export const DOUBLE_CLICK_MS = 500;
export const DRAG_THRESHOLD_PX = 5;
export const WHEEL_CODE = 'Wheel';

const modifierOrder = Object.values(InputModifier);
const triggers = new Set<string>(Object.values(InputTrigger));
const modifierCodes = new Set([
    'ControlLeft',
    'ControlRight',
    'ShiftLeft',
    'ShiftRight',
    'AltLeft',
    'AltRight',
    'MetaLeft',
    'MetaRight'
]);
const mouseButtonLabels: Record<MouseButton, string> = {
    [MouseButton.Left]: 'LMB',
    [MouseButton.Middle]: 'MMB',
    [MouseButton.Right]: 'RMB'
};
const triggerLabels: Record<InputTrigger, string> = {
    [InputTrigger.Click]: '',
    [InputTrigger.DoubleClick]: ' 2x',
    [InputTrigger.Drag]: ' Drag'
};
const codeLabels: Record<string, string> = {
    Escape: 'Esc',
    Delete: 'Del',
    ArrowLeft: '\u2190',
    ArrowRight: '\u2192',
    ArrowUp: '\u2191',
    ArrowDown: '\u2193',
    [WHEEL_CODE]: 'Wheel'
};

export const mouseCode = (button: number): string => `Mouse${button}`;

export function getChordDevice(code: string): InputDevice {
    if (code === WHEEL_CODE) {
        return InputDevice.Wheel;
    }
    return code.startsWith('Mouse') ? InputDevice.Mouse : InputDevice.Keyboard;
}

export function getMouseButton(code: string): MouseButton | undefined {
    const button = Number(code.slice('Mouse'.length));
    return button in mouseButtonLabels ? (button as MouseButton) : undefined;
}

export function formatChord({ modifiers, code, trigger }: ParsedInputChord): InputChord {
    const prefix = modifierOrder.filter((modifier) => modifiers.includes(modifier)).map((modifier) => `${modifier}+`);
    return `${prefix.join('')}${code}${trigger ? `@${trigger}` : ''}`;
}

export function parseChord(chord: string): ParsedInputChord | null {
    const [body, trigger, ...rest] = chord.split('@');
    const segments = body.split('+');
    const code = segments.pop();
    if (!code || rest.length > 0 || !segments.every((segment) => modifierOrder.includes(segment as InputModifier))) {
        return null;
    }

    const isMouse = getChordDevice(code) === InputDevice.Mouse;
    if (isMouse ? getMouseButton(code) === undefined || !triggers.has(trigger) : trigger !== undefined) {
        return null;
    }
    return { modifiers: segments as InputModifier[], code, trigger: trigger as InputTrigger | undefined };
}

export function getChordCode(chord: InputChord): string {
    return chord.split('@')[0].split('+').pop() ?? '';
}

const modifierEventKeys = {
    [InputModifier.Ctrl]: 'ctrlKey',
    [InputModifier.Shift]: 'shiftKey',
    [InputModifier.Alt]: 'altKey',
    [InputModifier.Meta]: 'metaKey'
} as const satisfies Record<InputModifier, keyof MouseEvent & keyof KeyboardEvent>;

export function getEventModifiers(event: KeyboardEvent | MouseEvent): InputModifier[] {
    return modifierOrder.filter((modifier) => event[modifierEventKeys[modifier]]);
}

/** Returns `null` for lone modifier keys - they only act as part of a chord. */
export function chordFromKeyboardEvent(event: KeyboardEvent, logicalKey = false): InputChord | null {
    const code = logicalKey && /^[a-z]$/i.test(event.key) ? `key:${event.key.toLowerCase()}` : event.code;
    return modifierCodes.has(event.code) ? null : formatChord({ modifiers: getEventModifiers(event), code });
}

export function chordFromMouseEvent(
    event: MouseEvent,
    trigger: InputTrigger,
    button: number = event.button
): InputChord {
    return formatChord({ modifiers: getEventModifiers(event), code: mouseCode(button), trigger });
}

export function chordFromWheelEvent(event: WheelEvent): InputChord {
    return formatChord({ modifiers: getEventModifiers(event), code: WHEEL_CODE });
}

function codeLabel(code: string, layoutMap?: KeyboardLayoutMap): string {
    if (/^key:[a-z]$/.test(code)) {
        return code.slice(4).toUpperCase();
    }
    const mouseButton = getMouseButton(code);
    if (getChordDevice(code) === InputDevice.Mouse && mouseButton !== undefined) {
        return mouseButtonLabels[mouseButton];
    }
    const layoutLabel = layoutMap?.get(code);
    if (layoutLabel && layoutLabel.trim()) {
        return /^[a-z]$/i.test(layoutLabel) ? layoutLabel.toUpperCase() : layoutLabel;
    }
    return codeLabels[code] ?? code.replace(/^(Key|Digit)/, '');
}

export function formatChordLabel(chord: InputChord, layoutMap?: KeyboardLayoutMap): string {
    const parsed = parseChord(chord);
    if (!parsed) {
        return chord;
    }
    const label = [...parsed.modifiers, codeLabel(parsed.code, layoutMap)].join('+');
    return parsed.trigger ? `${label}${triggerLabels[parsed.trigger]}` : label;
}
